import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import Paywall from '../components/Paywall';
import CountrySelect from '../components/CountrySelect';
import { useSubscription } from '../components/SubscriptionContext';
import { placeCall, primeCallToken } from '../services/callApi';
import { DEFAULT_COUNTRY } from '../constants/countries';
import { ensureCountryCode } from '../utils/phone';
import './Services.css';

const callFeatures = [
  'Place calls with notes from the dashboard',
  'Schedule calls for later (API-ready stub)',
  'Razorpay-guarded access per email',
];

const Calls = () => {
  const { user } = useAuth0();
  const email = user?.email;
  const { isSubscribedForEmail, isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [scheduleTo, setScheduleTo] = useState('');
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [activeTab, setActiveTab] = useState('call');
  const [isCalling, setIsCalling] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(null); // 'success' | 'error'
  const [callCountry, setCallCountry] = useState(DEFAULT_COUNTRY);
  const [scheduleCountry, setScheduleCountry] = useState(DEFAULT_COUNTRY);

  const isSubscribed = isSubscribedForEmail(email) || isFeatureUnlocked('call');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'schedule') {
      setActiveTab('schedule');
    } else {
      setActiveTab('call');
    }
  }, [location.search]);

  useEffect(() => {
    if (user) {
      primeCallToken().catch((err) => console.error('Call token warm-up failed', err));
    }
  }, [user]);

  if (!isSubscribed) {
    return (
      <div className="service-page">
          <Paywall
            title="Unlock Calls"
            description="Subscribe for ₹5 to place or schedule calls from your dashboard."
            onSubscribe={() => subscribeForFeature('call')}
            ctaLabel="Subscribe for ₹5"
            priceLabel="₹5"
            features={callFeatures}
          />
      </div>
    );
  }

  const handleCall = async (e) => {
    e.preventDefault();
    try {
      setIsCalling(true);
      setStatusMessage('');
      setStatusType(null);
      const normalizedTo = ensureCountryCode(to, callCountry?.dialCode || DEFAULT_COUNTRY.dialCode);
      const result = await placeCall(normalizedTo, message);
      setStatusType('success');
      setStatusMessage(result?.message || 'Call placed successfully');
      toast.success(result?.message || 'Call placed successfully');
    } catch (err) {
      console.error(err);
      setStatusType('error');
      setStatusMessage(`Failed to place call: ${err.message}`);
      toast.error(`Failed to place call: ${err.message}`);
    } finally {
      setIsCalling(false);
    }
  };

  const handleSchedule = (e) => {
    e.preventDefault();
    const normalizedTo = ensureCountryCode(scheduleTo, scheduleCountry?.dialCode || DEFAULT_COUNTRY.dialCode);
    console.info('Schedule call payload', { to: normalizedTo, scheduleMessage, scheduleTime });
    alert('Call scheduled (stub). Integrate your scheduling API.');
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('mode', tab === 'schedule' ? 'schedule' : 'call');
    navigate({ pathname: '/dashboard/calls', search: params.toString() }, { replace: true });
  };

  return (
    <div className="service-page">
      <div className="service-heading">
        <div>
          <h1>Calls</h1>
          <p className="service-description">Unlock for ₹5 to place or schedule calls.</p>
        </div>
        <div className="service-actions">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'call' ? 'active' : ''}`}
            onClick={() => switchTab('call')}
          >
            Make Call
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => switchTab('schedule')}
          >
            Schedule Call
          </button>
        </div>
      </div>
      {activeTab === 'call' && (
        <div className="service-grid">
          <form className="service-form" onSubmit={handleCall}>
            <h2>Make Call</h2>
            <label>
              To (phone number)
              <div className="phone-input-group">
                <CountrySelect
                  value={callCountry}
                  onChange={setCallCountry}
                  ariaLabel="Select country for call"
                  selectId="call-country-select"
                />
                <input
                  type="tel"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="XXXXXXXXXX"
                  required
                />
              </div>
            </label>
            <label>
              Call Message / Notes
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} required />
            </label>
            <button type="submit" className="primary-btn" disabled={isCalling}>{isCalling ? 'Placing…' : 'Place Call'}</button>
            {statusMessage && (
              <div className={`status-banner ${statusType || ''}`}>
                {statusType === 'success' && <span className="status-icon check">✓</span>}
                {statusType === 'error' && <span className="status-icon error">!</span>}
                <span>{statusMessage}</span>
              </div>
            )}
          </form>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="service-grid">
          <form className="service-form" onSubmit={handleSchedule}>
            <h2>Schedule Call</h2>
            <label>
              To (phone number)
              <div className="phone-input-group">
                <CountrySelect
                  value={scheduleCountry}
                  onChange={setScheduleCountry}
                  ariaLabel="Select country for scheduled call"
                  selectId="schedule-call-country-select"
                />
                <input
                  type="tel"
                  value={scheduleTo}
                  onChange={(e) => setScheduleTo(e.target.value)}
                  placeholder="XXXXXXXXXX"
                  required
                />
              </div>
            </label>
            <label>
              Call Message / Notes
              <textarea value={scheduleMessage} onChange={(e) => setScheduleMessage(e.target.value)} required />
            </label>
            <label>
              Schedule at
              <input type="datetime-local" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} required />
            </label>
            <button type="submit" className="primary-btn">Schedule Call</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Calls;
